import { useCallback, useEffect, useState } from 'react';

import { getStatusFromBooleanFlags, StandardReactQueryStatusInfo } from '../../reactQueryLib';
import { AUTH_RECORD_MAX_AGE_MS, AUTH_SESSION_KEY, useAuthPersistence } from '../AuthPersistenceContext';

import { PersistentAuthSession } from '.';

export const usePersistentAuthSession = () => {
  const cache = useAuthPersistence<PersistentAuthSession>();
  const [credentials, setCredentials] = useState<PersistentAuthSession | null>(null);
  const [isInitialized, setIsInitialized] = useState(false);

  useEffect(() => {
    cache.get(AUTH_SESSION_KEY).then(value => {
      setCredentials(value ?? null);
      setIsInitialized(true);
    });

    const unsubscribe = cache.subscribe(AUTH_SESSION_KEY, entry => {
      setCredentials(entry.value ?? null);
    });

    return unsubscribe;
  }, [cache]);

  const saveAuthCredentials = useCallback(async (newCredentials: Omit<PersistentAuthSession, '_id' | '_rev'> & { userId?: string }) => {
    const session: PersistentAuthSession = { _id: AUTH_SESSION_KEY, ...newCredentials };
    await cache.set(AUTH_SESSION_KEY, session, Date.now() + AUTH_RECORD_MAX_AGE_MS);
  }, [cache]);

  const isReady = !!credentials;
  const isLoading = !isInitialized;

  const standardReactQueryStatusInfo: StandardReactQueryStatusInfo = {
    isLoading,
    isReady,
    isError: false,
    isMissing: false,
    errorInfo: undefined,
    status: getStatusFromBooleanFlags(isLoading, false, false, isReady),
    progress: isInitialized ? 1 : 0,
    refetch: async () => {
      throw new Error('Refetch not supported');
    },
  };

  return { credentials, isInitialized, saveAuthCredentials, ...standardReactQueryStatusInfo };
};
